import { type ReactNode } from 'react';
import FocusLock from 'react-focus-lock';
import { AnimatePresence, motion } from 'framer-motion';

type ModalProps = {
  open: boolean;
  onClose?: () => void;
  /** When false, clicking the scrim does nothing (critical modals). */
  dismissOnScrim?: boolean;
  role?: 'dialog' | 'alertdialog';
  labelledBy?: string;
  children: ReactNode;
};

/**
 * Base modal primitive: scrim, focus trap, escape/scrim dismissal.
 * Critical modals (idle, print error) pass dismissOnScrim={false}.
 */
export function Modal({
  open,
  onClose,
  dismissOnScrim = true,
  role = 'dialog',
  labelledBy,
  children,
}: ModalProps) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="bg-ink/50 fixed inset-0 z-50 flex items-center justify-center px-8"
          onClick={() => dismissOnScrim && onClose?.()}
        >
          <FocusLock returnFocus>
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              role={role}
              aria-modal="true"
              aria-labelledby={labelledBy}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-card bg-surface p-8 shadow-2xl"
            >
              {children}
            </motion.div>
          </FocusLock>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
